import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAccessToken } from "hooks/useAccessToken";
import { BACKEND_DOMAIN } from "utils/constants";
import { buildRequestHeaders } from "utils/helpers";

export const chatbotDocumentsQueryKey = ["chatbotDocuments"];

type ParsingStatus =
    | "pending"
    | "parsing"
    | "completed"
    | "non-parsable"
    | "failed"
    | "skipped"
    | "too_big_for_parsing";

interface Document {
    upload_uid: string;
    file_name: string;
    mime_type: string;
    created_at: string | null;
    parsing_status: ParsingStatus;
    parsing_error?: string | null;
    has_blocks?: boolean;
}

interface AttachedDocument {
    upload_uid: string;
    file_name: string;
    mime_type: string;
    added_at: string;
}

interface DocumentContextCapacity {
    current_tokens: number;
    max_tokens: number;
    remaining_tokens: number;
    current_documents: number;
}

const PARSE_POLL_INTERVAL_MS = 5000;

const useChatbotDocumentsQuery = (buildingModelUid: string | null) => {
    const accessToken = useAccessToken();

    return useQuery({
        queryKey: [chatbotDocumentsQueryKey, buildingModelUid],
        queryFn: async () => {
            if (!buildingModelUid) {
                return Promise.resolve([]);
            }

            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/documents/${buildingModelUid}`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json() as Promise<Document[]>;
        },
        enabled: !!buildingModelUid,
        refetchInterval: (data: Document[] | undefined) => {
            if (!data) return false;
            const needsPolling = data.some(
                (d) => d.parsing_status === "pending" || d.parsing_status === "parsing"
            );
            return needsPolling ? PARSE_POLL_INTERVAL_MS : false;
        },
    });
};

const useAddChatbotDocumentsMutation = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            buildingModelUid,
            documentUids,
        }: {
            buildingModelUid: string;
            documentUids: string[];
        }) => {
            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/documents/${buildingModelUid}`;
            const response = await fetch(URL, {
                method: "POST",
                headers: {
                    ...buildRequestHeaders(accessToken),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ document_uids: documentUids }),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json();
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "attached"],
            });
            queryClient.invalidateQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "capacity"],
            });
            queryClient.refetchQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "capacity"],
            });
        },
    });
};

const useRemoveChatbotDocumentMutation = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            buildingModelUid,
            uploadUid,
        }: {
            buildingModelUid: string;
            uploadUid: string;
        }) => {
            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/documents/${buildingModelUid}/${uploadUid}`;
            const response = await fetch(URL, {
                method: "DELETE",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json();
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "attached"],
            });
            queryClient.invalidateQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "capacity"],
            });
            queryClient.refetchQueries({
                queryKey: [chatbotDocumentsQueryKey, variables.buildingModelUid, "capacity"],
            });
        },
    });
};

const useAttachedChatbotDocumentsQuery = (buildingModelUid: string | null) => {
    const accessToken = useAccessToken();

    return useQuery({
        queryKey: [chatbotDocumentsQueryKey, buildingModelUid, "attached"],
        queryFn: async () => {
            if (!buildingModelUid) {
                return Promise.resolve([]);
            }

            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/documents/${buildingModelUid}/attached`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json() as Promise<AttachedDocument[]>;
        },
        enabled: !!buildingModelUid,
    });
};

const useDocumentContextCapacityQuery = (buildingModelUid: string | null) => {
    const accessToken = useAccessToken();

    return useQuery({
        queryKey: [chatbotDocumentsQueryKey, buildingModelUid, "capacity"],
        queryFn: async () => {
            if (!buildingModelUid) {
                return Promise.resolve(null);
            }

            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/documents/${buildingModelUid}/capacity`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json() as Promise<DocumentContextCapacity>;
        },
        enabled: !!buildingModelUid,
    });
};

export { useAddChatbotDocumentsMutation, useAttachedChatbotDocumentsQuery, useChatbotDocumentsQuery, useDocumentContextCapacityQuery, useRemoveChatbotDocumentMutation };

    export type { AttachedDocument, Document, DocumentContextCapacity, ParsingStatus };

